/**
 * What the author is looking at (spec 031 v3, D-20): each surface that hosts
 * an assistant chat registers a getter while it is shown, and the assistant
 * store reads the current one just before it posts a turn, so the view is the
 * one at the moment of Send (or Retry). The last registered getter wins.
 */
import { useEffect, useRef } from 'react';
import type { AssistantView } from '../../../../Shared/src/@types';

export type AssistantViewGetter = () => AssistantView | undefined;

const getters: AssistantViewGetter[] = [];

/** Register `getter`; the returned function removes it (only it). */
export function registerAssistantView(getter: AssistantViewGetter): () => void {
  getters.push(getter);
  return () => {
    const at = getters.lastIndexOf(getter);
    if (at >= 0) getters.splice(at, 1);
  };
}

/** The view of the surface registered last, or undefined when none is. */
export function currentAssistantView(): AssistantView | undefined {
  const getter = getters[getters.length - 1];
  if (!getter) return undefined;
  try {
    return getter();
  } catch {
    return undefined;
  }
}

/** Tests: forget every registered surface. */
export function resetAssistantViews(): void {
  getters.length = 0;
}

/**
 * Register the surface's view while it is mounted and `enabled`; `getter` is
 * read at send time, so it may read refs and stores freely.
 */
export function useAssistantView(enabled: boolean, getter: AssistantViewGetter): void {
  const ref = useRef(getter);
  ref.current = getter;
  useEffect(() => {
    if (!enabled) return undefined;
    return registerAssistantView(() => ref.current());
  }, [enabled]);
}

/** A whole-ms moment from a player's frame time; undefined when it is not a number. */
export const momentOf = (t: number | null | undefined): number | undefined =>
  typeof t === 'number' && Number.isFinite(t) && t >= 0 ? Math.round(t) : undefined;

const SELECTION_KINDS = new Set(['sprite', 'dialog', 'sound', 'background']);

/** What the author sees under the scene hood (spec 031 v3 D-17), from the scene store. */
export function hoodAssistantView(sc: {
  previewOrientation: AssistantView['orientation'];
  playheadPosition: number;
  isPlaying: boolean;
  selection: { ids: string[]; type: string | null };
}): AssistantView {
  const { type, ids } = sc.selection;
  const atMs = Number.isFinite(sc.playheadPosition) && sc.playheadPosition >= 0 ? Math.round(sc.playheadPosition) : undefined;
  return {
    surface: 'hood',
    orientation: sc.previewOrientation,
    ...(atMs !== undefined ? { atMs } : {}),
    playing: sc.isPlaying,
    ...(type && SELECTION_KINDS.has(type) && ids.length
      ? { selection: { kind: type as NonNullable<AssistantView['selection']>['kind'], ids: [...ids] } }
      : {}),
  };
}
